import { useEffect, useMemo } from 'react';
import { ArrowBack } from '@mui/icons-material';
import { Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  ScenarioCreateInput,
  useAdminScenarioPlansQuery,
  useAdminScenarioQuery,
  useCreateScenarioMutation,
} from '../../../../../api/gql';
import { FullSizeLoader } from '../../../../../components/FullSizeLoader';
import { PageHeader } from '../../../../../components/PageHeader';
import { PageContent, PageLayout } from '../../../../../layout';
import { ScenarioForm } from '../../../../../modules/scenarios/ScenarioForm';
import { notify } from '../../../../../utils/notification';
import { useSafeNavigation } from '../../../../../hooks/useSafeNavigation';

const TRANS_NS = 'universal-reminding';

export const DuplicateScenarioPage = () => {
  const { t } = useTranslation(TRANS_NS, { keyPrefix: 'scenarios.duplicate' });
  const { safeNavigate } = useSafeNavigation();
  const { scenarioId } = useParams();

  const { mutate, isPending: isMutating } = useCreateScenarioMutation({
    onSuccess: (data) => {
      notify.positive({ message: t('mutation.success') });
      safeNavigate(`/admin/universal-reminding/scenarios/${data.createScenario.id}`);
    },
    onError: (error) => {
      console.error(error);
      notify.negative({
        message: t('mutation.error'),
      });
    },
  });

  const { data, isLoading, error } = useAdminScenarioQuery({
    id: scenarioId ?? '',
  });

  useEffect(() => {
    if (error) {
      console.error(error);
      notify.negative({
        message: t('query.error'),
      });
    }
  }, [error, t]);

  const { data: scenarioPlans, isLoading: isPlansLoading, isError: isPlansError } = useAdminScenarioPlansQuery();

  const onSubmit = (data: ScenarioCreateInput) => {
    mutate({
      input: {
        ...data,
      },
    });
  };

  const defaultValues: ScenarioCreateInput | undefined = useMemo(
    () =>
      data?.adminScenario
        ? {
            plan: data?.adminScenario.plan.name,
            subject: data?.adminScenario.subject,
            textCall: data?.adminScenario.textCall,
            textSms: data?.adminScenario.textSms,
            textEmail: data?.adminScenario.textEmail,
            name: data?.adminScenario.name,
            modelId: data?.adminScenario.model.id,
            userId: data?.adminScenario.user?.id,
          }
        : undefined,
    [data],
  );

  return (
    <PageLayout>
      <PageHeader title={t('title')} backRouteTo={'/admin/universal-reminding/scenarios'} Icon={ArrowBack} />
      <PageContent sx={{ justifyContent: 'flex-start', alignItems: 'flex-start' }}>
        <Paper sx={{ width: '100%', p: 4 }}>
          {isPlansLoading ? <FullSizeLoader /> : null}
          {isPlansError ? <div>Nedaří se načíst plány scénářů. Zkuste to prosím později.</div> : null}
          {data?.adminScenario && scenarioPlans ? (
            <ScenarioForm
              defaultValues={defaultValues}
              onSubmit={onSubmit}
              isLoading={isLoading || isMutating}
              isDisabled={isLoading || isMutating}
              scenarioPlans={scenarioPlans.scenarioPlans}
            />
          ) : null}
        </Paper>
      </PageContent>
    </PageLayout>
  );
};
